import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Level {
  level: number;
  subLevels: number[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;

interface Guess {
  value: number;
  kind: 'low' | 'high' | 'ok';
}

function fmt(n: number): string {
  return n.toLocaleString('pt-BR');
}

export function mountGuessNumber(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const totalSubs = level.subLevels.length;
  let subIndex = 0;
  let correctCount = 0;
  let totalAttempts = 0;

  let maxNumber = level.subLevels[0]!;
  let secret = 0;
  let rangeMin = 1;
  let rangeMax = maxNumber;
  let history: Guess[] = [];
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Adivinhe o número</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage guess">
      <div class="feedback" aria-live="polite"></div>
      <div style="display:flex;gap:var(--space-3);align-items:center;justify-content:space-between;max-width:480px;width:100%;padding:0 var(--space-3);">
        <p class="guess__progress" aria-live="polite"></p>
        <p class="guess__attempts" aria-live="polite"></p>
      </div>
      <div class="guess__prompt" aria-live="polite"></div>
      <div class="guess__feedback-msg" aria-live="polite"></div>
      <div class="guess__range-wrap">
        <div class="guess__range"></div>
      </div>
      <div class="guess__input-row">
        <input type="number" inputmode="numeric" class="guess__input" />
        <button class="btn btn--primary guess__guess-btn" type="button">Adivinhar</button>
      </div>
      <div class="guess__history"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.guess__progress')!;
  const attemptsEl = page.querySelector<HTMLElement>('.guess__attempts')!;
  const promptEl = page.querySelector<HTMLElement>('.guess__prompt')!;
  const feedbackMsgEl = page.querySelector<HTMLElement>('.guess__feedback-msg')!;
  const rangeEl = page.querySelector<HTMLElement>('.guess__range')!;
  const inputEl = page.querySelector<HTMLInputElement>('.guess__input')!;
  const guessBtn = page.querySelector<HTMLButtonElement>('.guess__guess-btn')!;
  const historyEl = page.querySelector<HTMLElement>('.guess__history')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  guessBtn.addEventListener('click', submitGuess);
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitGuess();
    }
  });

  function startSubLevel(): void {
    maxNumber = level.subLevels[subIndex]!;
    secret = Math.floor(Math.random() * maxNumber) + 1;
    rangeMin = 1;
    rangeMax = maxNumber;
    history = [];
    buttonsBlocked = false;
    inputEl.value = '';
    inputEl.min = '1';
    inputEl.max = String(maxNumber);
    feedbackMsgEl.textContent = '';
    feedbackMsgEl.className = 'guess__feedback-msg';
    renderAll();
    window.setTimeout(() => inputEl.focus(), 100);
  }

  function renderAll(): void {
    renderProgress();
    renderPrompt();
    renderRange();
    renderHistory();
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${subIndex + 1} de ${totalSubs}`;
    attemptsEl.textContent = `Tentativas: ${history.length}`;
  }

  function renderPrompt(): void {
    promptEl.innerHTML = `Adivinhe um número entre <strong>${fmt(rangeMin)}</strong> e <strong>${fmt(rangeMax)}</strong>.`;
  }

  function renderRange(): void {
    rangeEl.innerHTML = '';
    const minLbl = document.createElement('div');
    minLbl.className = 'guess__range-min';
    minLbl.textContent = fmt(rangeMin);
    rangeEl.appendChild(minLbl);
    const maxLbl = document.createElement('div');
    maxLbl.className = 'guess__range-max';
    maxLbl.textContent = fmt(rangeMax);
    rangeEl.appendChild(maxLbl);
    for (const g of history) {
      if (g.kind === 'ok') continue;
      const pct = ((g.value - 1) / (maxNumber - 1)) * 100;
      const mark = document.createElement('div');
      mark.className = `guess__range-mark guess__range-mark--${g.kind === 'low' ? 'low' : 'high'}`;
      mark.style.left = `${Math.max(2, Math.min(98, pct))}%`;
      mark.textContent = fmt(g.value);
      rangeEl.appendChild(mark);
    }
  }

  function renderHistory(): void {
    historyEl.innerHTML = '';
    for (const g of history) {
      const chip = document.createElement('span');
      chip.className = `guess__chip guess__chip--${g.kind}`;
      const arrow = g.kind === 'low' ? '↑' : g.kind === 'high' ? '↓' : '✓';
      chip.textContent = `${fmt(g.value)} ${arrow}`;
      historyEl.appendChild(chip);
    }
  }

  function submitGuess(): void {
    if (buttonsBlocked) return;
    const raw = inputEl.value.trim();
    if (!raw) return;
    const v = parseInt(raw, 10);
    if (!Number.isFinite(v)) return;
    if (v < 1 || v > maxNumber) {
      feedbackMsgEl.textContent = `Digite um número entre 1 e ${fmt(maxNumber)}.`;
      feedbackMsgEl.className = 'guess__feedback-msg';
      ctx.audio.play('wrong');
      return;
    }
    totalAttempts++;
    if (v === secret) {
      history.push({ value: v, kind: 'ok' });
      buttonsBlocked = true;
      ctx.audio.play('correct');
      feedbackMsgEl.innerHTML = `🎉 Acertou! O número era <strong>${fmt(secret)}</strong>.`;
      feedbackMsgEl.className = 'guess__feedback-msg';
      showFeedback('ok');
      correctCount++;
      renderAll();
      window.setTimeout(() => {
        subIndex++;
        if (subIndex < totalSubs) startSubLevel();
        else finishLevel();
      }, 1300);
    } else if (v < secret) {
      history.push({ value: v, kind: 'low' });
      if (v + 1 > rangeMin) rangeMin = v + 1;
      ctx.audio.play('click');
      feedbackMsgEl.innerHTML = `O número é <strong>MAIOR</strong> que ${fmt(v)}.`;
      feedbackMsgEl.className = 'guess__feedback-msg guess__feedback-msg--higher';
      renderAll();
      inputEl.value = '';
      inputEl.focus();
    } else {
      history.push({ value: v, kind: 'high' });
      if (v - 1 < rangeMax) rangeMax = v - 1;
      ctx.audio.play('click');
      feedbackMsgEl.innerHTML = `O número é <strong>MENOR</strong> que ${fmt(v)}.`;
      feedbackMsgEl.className = 'guess__feedback-msg guess__feedback-msg--lower';
      renderAll();
      inputEl.value = '';
      inputEl.focus();
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você adivinhou ${correctCount} de ${totalSubs} números em ${totalAttempts} tentativas.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/guessnumber/${level.level + 1}`;
      });
    }
  }

  startSubLevel();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
